import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { Flow } from 'types';
import { courses } from '../../services/api';
import { buildFlowPath } from '../../utils/flowNavigation';
import { DAILY_CHECKIN_FLOW_NAME, INTAKE_ASSESSMENT_FLOW_NAME } from 'constants/flows';
import { useAuthWithIntakeStatus } from 'hooks/useAuthWithIntakeStatus';
import { IntakeAssessmentComplete } from './IntakeAssessmentComplete';
import { ModuleCompletionCard } from './ModuleCompletionCard';
import type { FlowCompletionContext } from './types';

export interface FlowCompletedProps {
  flow: Flow;
  completionContext: FlowCompletionContext;
  courseId?: number | null;
}

export const FlowCompleted: React.FC<FlowCompletedProps> = ({
  flow,
  completionContext,
  courseId: initialCourseId = null,
}) => {
    const navigate = useNavigate();
    const continuation = completionContext?.continuation;
    const [courseId, setCourseId] = useState<number | null>(continuation?.course?.id ?? null);
    const [courseName, setCourseName] = useState<string | null>(continuation?.course?.name ?? null);
    const [nextFlowTitle, setNextFlowTitle] = useState<string | null>(continuation?.next_flow?.title || continuation?.next_flow?.name || null);
    const [nextChapterTitle, setNextChapterTitle] = useState<string | null>(null);
    const [nextFlowName, setNextFlowName] = useState<string | null>(continuation?.next_flow?.name ?? null);
    const [isResolvingNextFlow, setIsResolvingNextFlow] = useState(false);
    const { user, updateUser, refetchIntakeStatus } = useAuthWithIntakeStatus();
    const intakeSyncRef = useRef(false); // Used by IntakeAssessmentComplete to track if intake status has been updated

    const navigateToCourseCompletion = useCallback(
      (
        resolvedCourseId: number,
        resolvedCourseName?: string | null,
        completedAt?: string | null,
        replace = false,
      ) => {
        navigate(
          {
            pathname: '/course_complete',
            search: `?courseId=${resolvedCourseId}`,
          },
          {
            replace,
            state: {
              courseId: resolvedCourseId,
              courseName: resolvedCourseName ?? undefined,
              completedFlowName: flow.name,
              completedAt: completedAt ?? new Date().toISOString(),
            },
          },
        );
      },
      [flow.name, navigate],
    );

    useEffect(() => {
      let isActive = true;
      if (continuation) {
        setCourseId(continuation.course?.id ?? null);
        setCourseName(continuation.course?.name ?? null);
        setNextFlowName(continuation.next_flow?.name ?? null);
        setNextFlowTitle(continuation.next_flow?.title || continuation.next_flow?.name || null);
        setNextChapterTitle(continuation.next_flow?.chapter?.id !== continuation.chapter?.id
          ? continuation.next_flow?.chapter?.title ?? null : null);
        setIsResolvingNextFlow(false);
        return;
      }

      const resolveNextFlow = async () => {
        if (flow.name === DAILY_CHECKIN_FLOW_NAME) {
          return;
        }
        setIsResolvingNextFlow(true);
        try {
          const response = await courses.getFlowByName(flow.name, { courseId: initialCourseId });
          if (!isActive) {
            return;
          }

          const course = response?.data?.course;
          const upcomingFlow = response?.data?.next_flow;
          const resolvedCourseId =
            course && typeof course.id === 'number' ? course.id : initialCourseId;
          const resolvedCourseName = typeof course?.name === 'string' ? course.name : null;
          const resolvedNextFlowName =
            upcomingFlow && typeof upcomingFlow.name === 'string' ? upcomingFlow.name : null;

          setCourseId(resolvedCourseId);
          setCourseName(resolvedCourseName);
          setNextFlowName(resolvedNextFlowName);
          setNextFlowTitle(upcomingFlow?.title || resolvedNextFlowName);
          setNextChapterTitle(upcomingFlow?.chapter?.id !== response?.data?.chapter?.id ? upcomingFlow?.chapter?.title ?? null : null);
        } catch (error) {
          console.warn('Unable to resolve next flow for', flow.name, error);
          if (isActive) {
            setCourseId(null);
            setCourseName(null);
            setNextFlowName(null);
          }
        } finally {
          if (isActive) {
            setIsResolvingNextFlow(false);
          }
        }
      };

      resolveNextFlow();

    return () => {
        isActive = false;
      };
    }, [flow.name, initialCourseId, navigateToCourseCompletion, continuation]);

    // Intake completion status update is now handled in IntakeAssessmentComplete
    // when the user clicks to leave the completion screen, not immediately on mount

    const handleContinue = useCallback(() => {
      if (nextFlowName) {
        navigate(
          buildFlowPath({
            flowName: nextFlowName,
            courseId,
          }),
        );
        return;
      }

      if (courseId != null) {
        navigateToCourseCompletion(courseId, courseName);
        return;
      }

      navigate('/training#courses');
    }, [courseId, courseName, navigate, navigateToCourseCompletion, nextFlowName]);

    const primaryActionLabel = nextFlowName ? `Next: ${nextFlowTitle || nextFlowName}` : courseId != null ? 'View Course Summary' : 'View Content';

    if (flow.name === DAILY_CHECKIN_FLOW_NAME) {
        return <Navigate to="/home" replace />;
    }

    if (flow.name === INTAKE_ASSESSMENT_FLOW_NAME) {
        return <IntakeAssessmentComplete
            user={user}
            updateUser={updateUser}
            refetchIntakeStatus={refetchIntakeStatus}
            intakeSyncRef={intakeSyncRef}
        />;
    }

    return (
        <main className="flex h-full w-full items-center justify-center">
            <ModuleCompletionCard
                key={flow.id}
                title={flow.title || flow.name}
                primaryActionLabel={primaryActionLabel}
                onContinue={handleContinue}
                onReturnToDashboard={() => navigate((courseId ?? initialCourseId) ? `/training?courseId=${courseId ?? initialCourseId}#courses` : '/training#courses')}
                returnLabel="Back to course outline"
                nextChapterTitle={nextChapterTitle}
                isResolvingNextFlow={isResolvingNextFlow}
            />
        </main>
    );
};




